import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import PaymentSuccessCard from '../components/payment/PaymentSuccessCard';
import paymentService from '../services/paymentService';
import orderService from '../services/orderService';

const PaymentSuccessPage = () => {
  const { paymentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [payment, setPayment] = useState(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchPaymentData();
  }, [paymentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPaymentData = async () => {
    try {
      setLoading(true);

      // Try to get payment data from navigation state first
      const statePaymentData = location.state?.paymentData;

      let paymentData;
      if (statePaymentData) {
        paymentData = statePaymentData;
      } else {
        // Fallback to API call if no state data
        paymentData = await paymentService.getPaymentStatus(paymentId);
      }

      // Fetch order data if we have an order ID
      let orderData = null;
      if (paymentData.order_id) {
        try {
          orderData = await orderService.getOrder(paymentData.order_id);
        } catch (orderError) {
          console.warn('Could not fetch order data:', orderError);
          // Continue without order data
        }
      }

      // Transform payment data to match expected format
      const transformedPayment = {
        id: paymentData.payment_id || paymentData.id,
        transaction_id: paymentData.transaction_id,
        amount: parseFloat(paymentData.amount || 0),
        amount_subtotal: parseFloat(paymentData.amount || 0), // Backend doesn't provide breakdown yet
        amount_tax: 0, // Backend doesn't provide breakdown yet
        amount_tip: 0, // Backend doesn't provide breakdown yet
        status: paymentData.status,
        method: paymentData.method || 'Online Payment',
        payment_date: paymentData.payment_date || paymentData.created_at || new Date().toISOString(),
        order: paymentData.order_id
      };

      setPayment(transformedPayment);
      setOrder(orderData);
      setLoading(false);

      // If payment is successful, redirect to menu page with success parameters
      if (paymentData.status === 'completed') {
        // Build menu URL with payment parameters
        const merchantParam = orderData ? `merchant=${orderData.merchant}` : '';
        const tableParam = orderData?.table ? `&table=${orderData.table.id || orderData.table}` : '';

        // Small delay to show success briefly before redirecting
        setTimeout(() => {
          const menuUrl = `/menu?${merchantParam}${tableParam}&payment=success&payment_id=${paymentId}&order_id=${paymentData.order_id}`;
          navigate(menuUrl);
        }, 2000);
      }

    } catch (err) {
      console.error('Error fetching payment data:', err);
      setError('Failed to load payment information. Please try again.');
      setLoading(false);
    }
  };


  const goToMenu = () => {
    navigate('/menu');
  };

  const goToOrderTracking = () => {
    navigate(`/order/${order.order_number}/status`);
  };

  const handlePrintReceipt = (paymentData, orderData) => {
    const printWindow = window.open('', '_blank');

    const receiptContent = `
      <html>
        <head>
          <title>Payment Receipt</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              line-height: 1.6;
            }
            .receipt {
              max-width: 600px;
              margin: 0 auto;
              padding: 20px;
              border: 2px solid #ddd;
              border-radius: 8px;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 2px solid #eee;
              padding-bottom: 20px;
            }
            .success-icon {
              color: #4caf50;
              font-size: 2em;
              margin-bottom: 10px;
            }
            .info-section {
              margin-bottom: 20px;
            }
            .info-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 8px;
              padding: 4px 0;
            }
            .info-label {
              font-weight: bold;
              color: #555;
            }
            .divider {
              border-top: 1px solid #ddd;
              margin: 20px 0;
            }
            .total-section {
              background-color: #f9f9f9;
              padding: 15px;
              border-radius: 5px;
              margin: 20px 0;
            }
            .total {
              font-size: 1.3em;
              font-weight: bold;
              color: #4caf50;
              border-top: 2px solid #4caf50;
              padding-top: 10px;
              margin-top: 10px;
            }
            .footer {
              text-align: center;
              margin-top: 30px;
              font-size: 0.9em;
              color: #666;
              border-top: 1px solid #eee;
              padding-top: 20px;
            }
            .items-list {
              background-color: #f9f9f9;
              padding: 15px;
              border-radius: 5px;
              margin: 15px 0;
            }
            .item-row {
              display: flex;
              justify-content: space-between;
              padding: 5px 0;
              border-bottom: 1px solid #eee;
            }
            .item-row:last-child {
              border-bottom: none;
            }
          </style>
        </head>
        <body>
          <div class="receipt">
            <div class="header">
              <svg class="success-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="48" height="48"><path fill-rule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clip-rule="evenodd" /></svg>
              <h1 style="color: #4caf50; margin: 0;">Payment Successful!</h1>
              <p style="margin: 10px 0 0 0; color: #666;">Thank you for your payment</p>
            </div>

            <div class="info-section">
              <h3 style="color: #333; margin-bottom: 15px;">Payment Details</h3>
              <div class="info-row">
                <span class="info-label">Transaction ID:</span>
                <span>${paymentData?.transaction_id || paymentData?.id || 'N/A'}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Payment Date:</span>
                <span>${paymentData?.payment_date ? new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }).format(new Date(paymentData.payment_date)) : 'N/A'}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Payment Method:</span>
                <span>${paymentData?.method || 'N/A'}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Status:</span>
                <span style="color: #4caf50; font-weight: bold;">Completed</span>
              </div>
            </div>

            ${orderData ? `
              <div class="divider"></div>
              <div class="info-section">
                <h3 style="color: #333; margin-bottom: 15px;">Order Details</h3>
                <div class="info-row">
                  <span class="info-label">Order Number:</span>
                  <span>${orderData.order_number || 'N/A'}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Table:</span>
                  <span>${orderData.table_number || orderData.table?.name || 'N/A'}</span>
                </div>

                ${orderData.items && orderData.items.length > 0 ? `
                  <div style="margin-top: 15px;">
                    <h4 style="margin-bottom: 10px;">Items Ordered:</h4>
                    <div class="items-list">
                      ${orderData.items.map(item => `
                        <div class="item-row">
                          <span>${item.quantity}x ${item.name}</span>
                          <span>$${parseFloat(item.total).toFixed(2)}</span>
                        </div>
                      `).join('')}
                    </div>
                  </div>
                ` : ''}
              </div>
            ` : ''}

            <div class="divider"></div>

            <div class="total-section">
              <h3 style="color: #333; margin-bottom: 15px;">Payment Summary</h3>
              ${paymentData?.amount_subtotal ? `
                <div class="info-row">
                  <span>Subtotal:</span>
                  <span>$${parseFloat(paymentData.amount_subtotal).toFixed(2)}</span>
                </div>
              ` : ''}
              ${paymentData?.amount_tax ? `
                <div class="info-row">
                  <span>Tax:</span>
                  <span>$${parseFloat(paymentData.amount_tax).toFixed(2)}</span>
                </div>
              ` : ''}
              ${paymentData?.amount_tip && paymentData.amount_tip > 0 ? `
                <div class="info-row">
                  <span>Tip:</span>
                  <span>$${parseFloat(paymentData.amount_tip).toFixed(2)}</span>
                </div>
              ` : ''}
              <div class="info-row total">
                <span>Total Paid:</span>
                <span>$${parseFloat(paymentData?.amount || paymentData?.amount_total || 0).toFixed(2)}</span>
              </div>
            </div>

            <div class="footer">
              <p><strong>Thank you for your business!</strong></p>
              <p>For any questions, please contact support.</p>
            </div>
          </div>

          <script>
            window.onload = function() {
              window.print();
            }
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(receiptContent);
    printWindow.document.close();

    showSnackbar('Receipt sent to printer', 'success');
  };

  const handleEmailReceipt = (paymentData, orderData) => {
    // TODO: Implement email receipt functionality
    showSnackbar('Email receipt feature coming soon', 'info');
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '60vh', justifyContent: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Box sx={{ textAlign: 'center' }}>
            Loading payment information...
          </Box>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <PaymentSuccessCard
        payment={payment}
        order={order}
        countdown={0}
        isRedirecting={false}
        onCancelRedirect={() => {}}
        onGoToMenu={goToMenu}
        onGoToOrderTracking={goToOrderTracking}
        onPrintReceipt={handlePrintReceipt}
        onEmailReceipt={handleEmailReceipt}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PaymentSuccessPage;
